import { useEffect, useState, useRef } from "react";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { useDispatch, useSelector } from "react-redux";
import { Toast } from "../../../../core/components/Toast";
import { useLocation, useNavigate } from "react-router-dom";
import { setSelectedChat } from "../../../chat/presentation/store/ChatSlice";
import { Chats } from "../../../chat/domain/entities/getChat";
import { LikeParams } from "../../../match/domain/entities/emitLikeParams";
import InfoNotificationAlert from "../../../admin/domain/entities/InfoNotificationAlert";
import { notificationReceived } from "../../../match/presentation/store/MatchSlice";
import useMatch from "../../../match/presentation/hook/useMatch";
import { ContentUserModal } from "../components/ContentUserModal";
import { ModalSimple } from "../../../../core/components/Modal";
import { NotificationFilledBG } from "../../../../core/icons/Notification";
import { LinkifiedText } from "../../../../core/components/LinkifiedText";

export const ToastNotification = ({ isNotification }: { isNotification: boolean }) => {
    const { getProfileConfig } = useSelector((state: RootState) => state.onboarding);
    const { notifications } = useSelector((state: RootState) => state.match);
	const { chats } = useSelector((state: RootState) => state.chat);
    const prevChatsRef = useRef<typeof chats>([]);
    const hasSeededChatsRef = useRef(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [modalState, setModalState] = useState({ type:"", open: false});
    const [infoUser, setInfoUser] = useState<LikeParams | undefined>(undefined);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const dispatch: AppDispatch = useDispatch();
	const navigate = useNavigate();
    const location = useLocation();
    const [rippleLike, setRippleLike] = useState(false);
	const [rippleDislike, setRippleDislike] = useState(false);
    const { handleLike, handleUpdateMatchesSearch } = useMatch();
    const { user } = useSelector((state: RootState) => state.onboarding);
    const [infoAlert, setInfoAlert] = useState<InfoNotificationAlert | undefined>(undefined);

    useEffect(() => {
        if (shouldDisplayNotification()) {
            const notification = notifications[currentIndex];
            const isNotificacionLikeOrMatch = (notification as LikeParams)?.type === "match" || (notification as LikeParams)?.type === "like";
            if (isNotificacionLikeOrMatch) {
                const noti: LikeParams = notification as LikeParams;
                if (noti.type === "like" && (noti as LikeParams & { hidden?: boolean }).hidden === true) {
                    setIsVisible(true);
                    setInfoUser(undefined);
                    return;
                }
                if (noti.type === "match") {
                    setIsVisible(true);
                    setInfoUser(noti);
                    return;
                }
                if (noti.type === "like" && noti.user_id === noti.user_id1) {
                    setIsVisible(true);
                    setInfoUser(noti);
                    return;
                } else {
                    setCurrentIndex((prevIndex) => prevIndex + 1);
                    return;
                }
            }
            timerRef.current = setTimeout(() => {
                setIsVisible(false);
                setTimeout(() => {
                    setIsVisible(true);
                    setCurrentIndex((prevIndex) => prevIndex + 1);
                }, 500);
            }, 6000);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [currentIndex, notifications]);

    useEffect(() => {
		if (!navigator.serviceWorker) return;
		const handleMessage = (event: MessageEvent) => {
			const event_id = event.data.id;
			const user_id = user?.id;

			if (event.data.type === "message") {
				let cleanedChatId = event_id.replace(user_id, "");
				cleanedChatId = cleanedChatId.replace(/^[-]+|[-]+$/g, "");
				const chat = chats.find((chat) => chat.chat_id === event_id);
				dispatch(setSelectedChat(chat as Chats));
				navigate(`/messages/${cleanedChatId}`);
			} else if (event.data.type === "partner") {
				navigate("/settings");
			} else if (event.data.type === "match" || event.data.type === "like") {
				const notification = notifications.find((notification) => (notification as LikeParams).match_id === event_id);
				if (notification) {
					setInfoUser(notification as LikeParams);
					setModalState({ type: event.data.type, open: true });
				}
			} else if (event.data.type === "admin") {
                const notification = notifications.find((notification) => (notification as InfoNotificationAlert).notification_id === event_id);
                if (notification) {
                    setInfoAlert(notification as InfoNotificationAlert);
                    setModalState({ type: event.data.type, open: true });
                }
			}
		};
		navigator.serviceWorker.addEventListener("message", handleMessage);
		return () => {
			navigator.serviceWorker.removeEventListener("message", handleMessage);
		};
	}, [navigate, dispatch, notifications]);

    useEffect(() => {
        const previousChatsById = new Map(
            prevChatsRef.current.map((chat) => [chat.chat_id, chat])
        );
        const isInMessagesRoute = location.pathname.startsWith("/messages");
        if (
            getProfileConfig?.search_filters.chat &&
            !isInMessagesRoute &&
            hasSeededChatsRef.current
        ) {
            chats.forEach((chat) => {
                const prevChat = previousChatsById.get(chat.chat_id);
                const currentUnread = Number(chat.unread_messages || 0);
                const previousUnread = Number(prevChat?.unread_messages || 0);
                const isIncomingMessage = chat.message_sender_id === chat.user_id && chat.read === false;
                const hasNewMessageId = Boolean(chat.message_id) && chat.message_id !== prevChat?.message_id;
                const becameUnreadWithoutMessageId = Boolean(prevChat) && !chat.message_id && previousUnread === 0 && currentUnread > 0;

                if (
                    isIncomingMessage &&
                    currentUnread > 0 &&
                    (hasNewMessageId || becameUnreadWithoutMessageId)
                ) {
                    dispatch(
                        notificationReceived({
                            title: "New Message",
                            subtitle: `${chat.username} sent a message`,
                            data: { type: "message", chat: chat }
                        })
                    );
                }
            });
        }
        prevChatsRef.current = chats;
        hasSeededChatsRef.current = true;
    }, [chats, dispatch, getProfileConfig?.search_filters.chat, location.pathname]);

    const handleCloseToast = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setInfoUser(undefined);
        setInfoAlert(undefined);
        setModalState({
            type: "",
            open: false
        });
        setCurrentIndex((prevIndex) => prevIndex + 1);
    };

    const handleActionToast = (data?: { type: string, chat?: Chats}) => {
        if (data?.type === "partner") {
            navigate(`/settings`);
            handleCloseToast();
            return;
        }
        if (data?.type === "message" && data.chat) {
            handleChatClick(data.chat);
            return;
        }
        setModalState({
            type: data?.type || "",
            open: true
        });
    }
    
	const handleChatClick = (chat: Chats) => {
		handleCloseToast();
		dispatch(setSelectedChat(chat));
		navigate(`/messages/${chat.user_id}`);
	};

    const handleLikeMatch = async (type: "like" | "dislike") => {
        if (!infoUser?.user_id) return;
        handleLike(infoUser.user_id, type);
        handleUpdateMatchesSearch(infoUser.user_id);
        const setRipple = type === "like" ? setRippleLike : setRippleDislike;
        setRipple(true);
        await new Promise(resolve => setTimeout(resolve, 300));
        setRipple(false);
        handleCloseToast();
    };

    const  getNotificationTitle= (notifications: any[], currentIndex: number): string => {
        const notification = notifications[currentIndex];
        if ((notification as InfoNotificationAlert).title) {
            return (notification as InfoNotificationAlert).title;
        }
        if ((notification as LikeParams).status_user_id1 === "matched" && (notification as LikeParams).status_user_id2 === "matched") {
            return "Matched";
        }
        return "Like";
    }

    const getNotificationMessage = (notifications: any[], currentIndex: number): JSX.Element => {
        const notification = notifications[currentIndex];
        if (notification?.type === "like" && notification?.hidden === true) {
            return <>Someone liked your profile</>;
        }
        if ((notification as InfoNotificationAlert).title) {
            return (
                <LinkifiedText text={(notification as InfoNotificationAlert).subtitle} />
            );
        }
        const user = (notification as LikeParams).username;
        const isMatched = (notification as LikeParams).status_user_id1 === "matched" && (notification as LikeParams).status_user_id2 === "matched";
        return (
            <>
                {isMatched ? (
                    <>
                    You matched with{" "}
                    <span className="text-primary font-semibold">{user}</span>
                    </>
                ) : (
                    <>
                    Hey{" "}
                    <span className="text-primary font-semibold">{user}</span>{" "}
                    has just liked your profile!
                    </>
                )}
            </>
        );
    }
    const getNotificationAction = (notifications: any[], currentIndex: number): { handleAction: () => void } | undefined => {
        const notification = notifications[currentIndex];
        if (notification?.type === "like" && notification?.hidden === true) {
            return undefined;
        }
        if (notification?.type === "like" || notification?.type === "match") {
            return { handleAction: () => handleActionToast({type: (notification as LikeParams)?.type || "" }) };
        } else if (notification?.data && (notification.data.type === "message" || notification.data.type === "partner")) {   
            return { handleAction: () => handleActionToast((notification as InfoNotificationAlert).data) };
        } else {
            return undefined;
        }
    }

    const shouldDisplayNotification = (): boolean => {
        const isIndexValid = currentIndex < notifications.length;
        if (notifications[currentIndex]?.type === "like" && (notifications[currentIndex] as any).hidden === true) {
            return isIndexValid;
        }
        if (notifications[currentIndex]?.type === "like") {
            if ((notifications[currentIndex] as LikeParams).user_id !== (notifications[currentIndex] as LikeParams).user_id1 ) return false;
        }
        return isIndexValid;
    }
      
    return (
        <>
            {shouldDisplayNotification() && (
                <Toast
                    title={getNotificationTitle(notifications, currentIndex)}
                    message={getNotificationMessage(notifications, currentIndex)}
                    className={`transform transition-transform duration-500 ease-out ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
                    handleClose={handleCloseToast}
                    {...getNotificationAction(notifications, currentIndex)}
                />
            )}
            <ContentUserModal
                modalState={modalState}
                infoUser={infoUser}
                handleCloseToast={handleCloseToast}
                handleChatClick={handleChatClick}
                handleLikeMatch={handleLikeMatch}
                rippleLike={rippleLike}
                rippleDislike={rippleDislike}
            />
            {modalState.type === "admin" && infoAlert && 
                <ModalSimple
                    title={infoAlert.title}
                    variant="secondary"
                    actionExecuted={modalState.open}
                    onClose={handleCloseToast}
                    icon={<NotificationFilledBG />}
                    next
                >
                    <LinkifiedText text={infoAlert.subtitle} />
                </ModalSimple>
            }
        </>
    )
}
